import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Bookmark, Copy, Link as LinkIcon, RefreshCw, Share2, Trash2 } from 'lucide-react';

export const SaveRouteDialog = ({
  open, onOpenChange,
  saveForm, setSaveForm,
  selectedStops, routeInfo,
  saving, saveRoute,
  currentRoute,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md" data-testid="save-route-dialog">
      <DialogHeader>
        <DialogTitle>{currentRoute ? 'Update Route' : 'Save Route'}</DialogTitle>
        <DialogDescription>
          Give your route a name so you can find it later. {selectedStops.length} stops · {routeInfo?.distanceMiles ? `${routeInfo.distanceMiles} mi` : '—'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 py-2">
        <div>
          <Label htmlFor="route-name">Name *</Label>
          <Input
            id="route-name"
            value={saveForm.name}
            onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })}
            placeholder="e.g. Colorado Aspens Loop"
            data-testid="route-name-input"
          />
        </div>
        <div>
          <Label htmlFor="route-desc">Description</Label>
          <Textarea
            id="route-desc"
            value={saveForm.description}
            onChange={(e) => setSaveForm({ ...saveForm, description: e.target.value })}
            placeholder="Optional notes for yourself or anyone you share this with"
            rows={3}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={saveRoute} disabled={saving} data-testid="confirm-save-route-btn">
          {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Bookmark className="h-4 w-4 mr-2" />}
          {currentRoute ? 'Update' : 'Save'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ShareRouteDialog = ({
  open, onOpenChange,
  currentRoute,
  buildShareUrl,
  copyShareUrl,
  shareRouteLink,
  regenerateShareToken,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md" data-testid="share-route-dialog">
      <DialogHeader>
        <DialogTitle>Share Route</DialogTitle>
        <DialogDescription>
          Anyone with this link can view your route — no login required.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 py-2">
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
          <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          <code
            className="flex-1 text-xs truncate select-all font-mono"
            data-testid="share-url-text"
          >
            {currentRoute?.share_token ? buildShareUrl(currentRoute.share_token) : '—'}
          </code>
          <Button size="sm" variant="ghost" onClick={copyShareUrl} title="Copy to clipboard" data-testid="copy-share-btn">
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Primary share action — opens the native share sheet on iOS/Android
            (iMessage/WhatsApp/Mail/Instagram), the browser's Web Share UI on
            mobile web, or falls back to clipboard on desktop. */}
        <Button
          onClick={shareRouteLink}
          className="w-full"
          data-testid="share-system-btn"
          disabled={!currentRoute?.share_token}
        >
          <Share2 className="h-4 w-4 mr-2" />
          Share…
        </Button>

        {currentRoute?.view_count > 0 && (
          <p className="text-xs text-muted-foreground">
            Viewed <strong>{currentRoute.view_count}</strong> {currentRoute.view_count === 1 ? 'time' : 'times'}
          </p>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={regenerateShareToken}
          className="w-full"
          data-testid="regenerate-share-btn"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-2" />
          Generate new link (revoke current)
        </Button>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Done</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const SavedRoutesDialog = ({
  open, onOpenChange,
  savedRoutes,
  loadRoute,
  deleteSavedRoute,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="saved-routes-dialog">
      <DialogHeader>
        <DialogTitle>Your Saved Routes</DialogTitle>
        <DialogDescription>
          {savedRoutes.length === 0
            ? 'You have no saved routes yet. Build a route and click Save to keep it here.'
            : `${savedRoutes.length} ${savedRoutes.length === 1 ? 'route' : 'routes'}`}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2 overflow-y-auto -mx-1 px-1">
        {savedRoutes.map((r) => (
          <div
            key={r.route_id}
            className="border border-border rounded-lg p-3 hover:bg-muted/40 transition-colors"
            data-testid={`saved-route-${r.route_id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{r.name}</p>
                {r.description && (
                  <p className="text-xs text-muted-foreground truncate">{r.description}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadRoute(r)}
                  data-testid={`load-route-${r.route_id}`}
                >
                  Load
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-destructive"
                  onClick={() => deleteSavedRoute(r.route_id)}
                  data-testid={`delete-route-${r.route_id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>{(r.stops || []).length} stops</span>
              {r.distance_km != null && <span>· {Math.round(r.distance_km * 0.621371) } mi</span>}
              {r.view_count > 0 && (
                <span>· {r.view_count} {r.view_count === 1 ? 'view' : 'views'}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);
